// Fixed product section identifiers only. Never capture DOM text, inputs, URLs or arbitrary IDs.
export const parentSectionSelectors = {
  course_heading: '[data-parent-stage="course_view"]',
  course_price: '[data-parent-stage="price_view"]',
  course_learning: '#course-learning',
  course_outline: '#course-outline',
  course_outcomes: '#course-outcomes',
  course_times: '#course-times',
  course_faq: '#course-faq',
  course_demo: '[data-parent-stage="demo_view"]',
} as const;
export interface ParentViewport {
  scroll_container: 'portal' | 'document';
  width: number;
  height: number;
  scroll_x: number;
  scroll_y: number;
  page_width: number;
  page_height: number;
  visible_sections: (keyof typeof parentSectionSelectors)[];
}
const bounded = (value: number, max: number, min = 0) =>
  Math.min(max, Math.max(min, Math.round(Number.isFinite(value) ? value : min)));

/** Event-time geometry, not reading, dwell time or session replay. */
export function readParentViewport(): ParentViewport {
  const visual = window.visualViewport;
  const scroller = document.querySelector<HTMLElement>('[data-testid="portal-scroll-region"]');
  const region = scroller?.getBoundingClientRect();
  const left = Math.max(visual?.offsetLeft ?? 0, region?.left ?? 0);
  const top = Math.max(visual?.offsetTop ?? 0, region?.top ?? 0);
  const right = Math.min(
    (visual?.offsetLeft ?? 0) + (visual?.width ?? window.innerWidth),
    region?.right ?? Infinity,
  );
  const bottom = Math.min(
    (visual?.offsetTop ?? 0) + (visual?.height ?? window.innerHeight),
    region?.bottom ?? Infinity,
  );
  const width = bounded(right - left, 16384, 1);
  const height = bounded(bottom - top, 16384, 1);
  const root = document.documentElement;
  const body = document.body;
  const visible_sections = (
    Object.keys(parentSectionSelectors) as (keyof typeof parentSectionSelectors)[]
  ).filter((key) =>
    Array.from(document.querySelectorAll(parentSectionSelectors[key])).some((node) => {
      const rect = node.getBoundingClientRect();
      const style = window.getComputedStyle(node);
      return (
        style.visibility !== 'hidden' &&
        style.display !== 'none' &&
        Math.min(rect.right, left + width) - Math.max(rect.left, left) > 0 &&
        Math.min(rect.bottom, top + height) - Math.max(rect.top, top) > 0
      );
    }),
  );
  return {
    scroll_container: scroller ? 'portal' : 'document',
    width,
    height,
    scroll_x: bounded(
      scroller ? scroller.scrollLeft + left - region!.left : (visual?.pageLeft ?? window.scrollX),
      1000000,
    ),
    scroll_y: bounded(
      scroller ? scroller.scrollTop + top - region!.top : (visual?.pageTop ?? window.scrollY),
      1000000,
    ),
    page_width: bounded(
      Math.max(
        width,
        scroller ? scroller.scrollWidth : root.scrollWidth,
        scroller ? 0 : (body?.scrollWidth ?? 0),
      ),
      1000000,
      1,
    ),
    page_height: bounded(
      Math.max(
        height,
        scroller ? scroller.scrollHeight : root.scrollHeight,
        scroller ? 0 : (body?.scrollHeight ?? 0),
      ),
      1000000,
      1,
    ),
    visible_sections,
  };
}
